import { omitBy } from "remeda";
import type { Job } from "../types/jobs";
import type { CreateQuoteSimpleDTO, Quote, QuotesDto, UpdateQuoteMarkersDto } from "../types/quotes";
import { apiService } from "./apiService";
import { getErrorMessage } from "./errorHandling";

export const createQuote = async (data: CreateQuoteSimpleDTO) => {
  try {
    return await apiService<Quote>({ method: "post", url: "/quotes/create/", data });
  } catch (error) {
    throw getErrorMessage(error);
  }
};

export const createCustomQuote = async ({
  data,
  isMultipart,
}: {
  data: Record<string, unknown>;
  isMultipart?: boolean;
}) => {
  try {
    return await apiService<Job>({ method: "post", url: "/quotes/create_custom_job/", data, isMultipart });
  } catch (error) {
    throw getErrorMessage(error);
  }
};

export const deleteQuote = async (id: number) => {
  try {
    return await apiService({ method: "delete", url: `/quotes/destroy/${id}/` });
  } catch (error) {
    throw getErrorMessage(error);
  }
};

export const getQuote = async (id: string) => {
  try {
    return await apiService<Quote>({ method: "get", url: `/quotes/detail/${id}/` });
  } catch (error) {
    throw getErrorMessage(error);
  }
};

export const getQuotes = async ({ jobNumber, page }: { jobNumber: string; page: number }) => {
  try {
    const params = omitBy({ job_number: jobNumber, page }, (value) => !value);
    return await apiService<QuotesDto>({ method: "get", url: "/quotes/list/", params });
  } catch (error) {
    throw getErrorMessage(error);
  }
};

export const updateQuoteMarkers = async (payload: { id: number; data: UpdateQuoteMarkersDto }) => {
  try {
    return await apiService({ method: "patch", url: `/quotes/set_job_quote_flag/${payload.id}/`, data: payload.data });
  } catch (error) {
    throw getErrorMessage(error);
  }
};

export const updateQuote = async ({ id, data }: { id: string; data: CreateQuoteSimpleDTO }) => {
  try {
    return await apiService<Quote>({ method: "patch", url: `/quotes/update/${id}/`, data });
  } catch (error) {
    throw getErrorMessage(error);
  }
};

export interface QuoteDataForCreateFlowResponseDto {
  calculation_res: Array<{
    item_name: string;
    quantity: number;
    unit: string;
    cost_per_unit: number;
    price_per_unit: number;
    is_editable: boolean;
  }>;
  salesman?: string;
  for_email?: string;
}

export const getQuoteDataForCreateFlow = async (id: string) => {
  try {
    return await apiService<QuoteDataForCreateFlowResponseDto>({
      method: "post",
      url: "/quotes/get_job_quote_calculation_data/",
      data: { job_number: id },
    });
  } catch (error) {
    throw getErrorMessage(error);
  }
};

export type QuoteReport = {
  status: string;
  result: null | string;
  error: null | string;
};

export const getQuoteReport = async (id: string) => {
  try {
    return await apiService<QuoteReport>({ method: "get", url: `/quotes/check_quote_report/${id}/` });
  } catch (error) {
    throw getErrorMessage(error);
  }
};
